import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TransportSegmentsApiService } from '@/bag/transport-segments/transport-segments.api.service';
import { Segment } from '@/bag/transport-segments/transport-segments.model';
import { TransportTermList } from '@/bag/bag-transport-term/bag-transport-term.model';
import { ProcessBagTransportTermData } from '@/bag/bag-transport-term/process-bag-transport-term-data';
import { BagTransportTermTableComponent } from '@/bag/bag-transport-term/bag-transport-term-table/bag-transport-term-table.component';
import { Router } from '@angular/router';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import { SegmentFilesDialogComponent } from '@/bag/transport-segments/segment-files-dialog/segment-files-dialog.component';
import { SegmentAssignTourismEventDialogComponent } from '@/bag/transport-segments/segment-assign-tourism-event-dialog/segment-assign-tourism-event-dialog.component';

type Column = keyof Pick<Segment, 'id' | 'name' | 'transportTerms'> | 'dateRange' | 'edit';

@Component({
	selector: 'app-transport-segments',
	standalone: true,
	imports: [
		CommonModule,
		NavbarModule,
		MatTableModule,
		MatPaginatorModule,
		MatButtonModule,
		MatTooltipModule,
		BagTransportTermTableComponent,
	],
	providers: [{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() }],
	templateUrl: './transport-segments.component.html',
	styleUrls: ['./transport-segments.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportSegmentsComponent implements OnInit {
	readonly tableParameters = signal({
		statuses: [
			{ value: 100, viewValue: '100%' },
			{ value: 75, viewValue: '75%' },
			{ value: 50, viewValue: '50%' },
			{ value: 25, viewValue: '25%' },
			{ value: 0, viewValue: '0%' },
		],
		organizationStatuses: [] as Array<{ id: number; name: string }>,
	});
	readonly segments = signal<Segment[]>([]);
	readonly page = signal(0);
	readonly pageSize = signal(10);
	readonly allItemsSize = signal(0);
	readonly responsibleUserHasChanged = signal(false);
	readonly columnsToDisplay: Column[] = ['id', 'name', 'dateRange', 'transportTerms'];
	readonly columnsToDisplayWithExpand: Array<Column | 'expand'> = [...this.columnsToDisplay, 'expand'];

	expandedElement: Segment | null = null;
	readonly segmentsCache = signal<Record<string, Segment[]>>({});
	readonly segmentTermsMap = signal<Record<number, TransportTermList[]>>({});
	readonly loadingSegmentTermsMap = signal<Record<number, boolean>>({});
	private readonly transportSegmentsApiService = inject(TransportSegmentsApiService);
	private readonly datePipe = inject(DatePipe);
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly matDialog = inject(MatDialog);

	ngOnInit(): void {
		this.loadOrganizationStatuses();
		this.loadSegments();
	}

	pageChanged(event: PageEvent): void {
		this.page.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.expandedElement = null;
		this.loadSegments();
	}

	isExpanded(element: Segment): boolean {
		return this.expandedElement === element;
	}

	toggle(element: Segment): void {
		if (this.isExpanded(element)) {
			this.expandedElement = null;
			return;
		}
		this.expandedElement = element;
		this.loadSegmentTerms(element.id);
	}

	getSegmentTerms(segmentId: number): TransportTermList[] {
		return this.segmentTermsMap()[segmentId] ?? [];
	}

	isSegmentTermsLoading(segmentId: number): boolean {
		return !!this.loadingSegmentTermsMap()[segmentId];
	}

	deadlineChanged(): void {
		const expandedElement = this.expandedElement;
		if (!expandedElement) {
			return;
		}
		this.segmentTermsMap.update((current) => {
			const next = { ...current };
			delete next[expandedElement.id];
			return next;
		});
		this.loadSegmentTerms(expandedElement.id);
	}

	onResponsibleUserHasChanged(value: boolean): void {
		this.responsibleUserHasChanged.set(value);
	}

	openAddSegmentPage(): void {
		const urlTree = this.router.createUrlTree(['/transport-segments/add']);
		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	openEditSegmentPage(segmentId: number): void {
		const urlTree = this.router.createUrlTree(['/transport-segments', segmentId]);
		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	openSegmentListsPage(segmentId: number): void {
		const urlTree = this.router.createUrlTree(['/segment', segmentId, 'list']);
		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	openSegmentFilesDialog(segmentId: number): void {
		this.matDialog.open(SegmentFilesDialogComponent, {
			data: segmentId,
			width: '70%',
			maxWidth: '900px',
		});
	}

	openAssignTourismEventDialog(segment: Segment): void {
		this.matDialog
			.open(SegmentAssignTourismEventDialogComponent, {
				data: segment,
				width: '600px',
				maxWidth: '90vw',
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((saved) => {
				if (!saved) {
					return;
				}
				this.segmentTermsMap.set({});
				this.loadingSegmentTermsMap.set({});
				this.expandedElement = null;
			});
	}

	private loadSegmentTerms(segmentId: number): void {
		if (this.segmentTermsMap()[segmentId]) {
			return;
		}

		this.loadingSegmentTermsMap.update((current) => ({
			...current,
			[segmentId]: true,
		}));

		this.transportSegmentsApiService
			.getTransportTermsBySegment(segmentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				const terms = new ProcessBagTransportTermData(this.datePipe).afterConvertData(result, []);
				this.segmentTermsMap.update((current) => ({
					...current,
					[segmentId]: terms,
				}));
				this.loadingSegmentTermsMap.update((current) => ({
					...current,
					[segmentId]: false,
				}));
			});
	}

	private loadSegments(): void {
		const page = this.page();
		const pageSize = this.pageSize();
		const cacheKey = `${page}-${pageSize}`;
		const cachedSegments = this.segmentsCache()[cacheKey];
		if (cachedSegments) {
			this.segments.set(this.sortSegmentsById(cachedSegments));
			return;
		}

		const offset = page * pageSize;
		this.transportSegmentsApiService
			.getSegments(pageSize, offset)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				const sortedSegments = this.sortSegmentsById(result.results);
				this.segments.set(sortedSegments);
				this.allItemsSize.set(result.count);
				this.segmentsCache.update((current) => ({
					...current,
					[cacheKey]: sortedSegments,
				}));
			});
	}

	private loadOrganizationStatuses(): void {
		this.transportSegmentsApiService
			.getOrganizationStatuses()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				this.tableParameters.update((current) => ({
					...current,
					organizationStatuses: result.results,
				}));
			});
	}

	private sortSegmentsById(segments: Segment[]): Segment[] {
		return [...segments].sort((a, b) => a.id - b.id);
	}
}
